import { sdk } from '../sdk'
import { getApiOrigin } from '../sdk-official/loader'
import { sanitizePath } from '../hooks/useGoogleAnalytics'
import { clearSessionToken, readSessionToken, storeSessionToken } from './session'

/**
 * F-88: the community's own analytics, next to (not instead of) its Google Analytics.
 *
 * Page views, the purchase funnel's first four steps and a heartbeat for "online now"
 * go to `POST /api/analytics/events` in batches. Nothing here may slow a page or throw:
 * every send is fire-and-forget and a failure is dropped silently.
 *
 * Session: the API issues it. The first batch goes without one and every answer carries a
 * signed `sessionToken`, sent back as `sessionId` on the next batches (and in the checkout
 * bodies, so an order is tied to the visit). An invalid or expired token is answered with a
 * new one, which replaces it.
 *
 * Privacy: the token lives in sessionStorage only (dies with the tab, dropped on
 * logout), paths go through the same sanitizer GA uses (no query, no tokens), and no
 * event ever carries what a person typed. The referrer and UTMs are read once, from the
 * URL the tab was opened with.
 */

export type FunnelStep = 'catalog' | 'product' | 'checkout_identify' | 'checkout_payment'

interface AnalyticsEvent {
  type: 'page_view' | 'funnel_step' | 'heartbeat' | 'post_expand' | 'post_link_click'
  path: string
  /** F-179: the post opened (page_view) or read in a card (post_expand, post_link_click). */
  contentId?: string | null
  /** F-179: the clicked href; the API keeps only its domain. */
  linkHost?: string
  spaceId?: string | null
  module?: string | null
  funnelStep?: FunnelStep
  productId?: string | null
  referrer?: string
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  occurredAt: string
}

const MAX_BATCH = 50
/* Never grow without bound while the community is still unknown or the API is down. */
const MAX_QUEUE = 200
const FLUSH_MS = 10_000
/* A presence event (page view, heartbeat) held back by the boot (community still unknown, first token on its way) retries this soon: "Online agora" must see an arrival within seconds (F-110). */
const PRESENCE_RETRY_MS = 1_000
const HEARTBEAT_MS = 60_000
const PAGE_VIEW_SETTLE_MS = 700
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
/* F-179: the modules whose item page (/{section}/{space}/{id}) is a post. */
const POST_MODULES = new Set(['content', 'feed'])
/* An item page waits this long for its space, so its page view can say which post was opened. */
const POST_HOLD_MS = 4_000
const POST_HOLD_RETRY_MS = 250

const isBrowser = typeof window !== 'undefined'
const queue: AnalyticsEvent[] = []
/* While a batch without a token is out, the next ones wait for its answer instead of opening sessions of their own. */
let awaitingToken = false
/* Bumped on logout: an answer to a batch sent before it must not bring the old session back. */
let generation = 0
let flushTimer: number | null = null
let started = false

/* Where the visitor is now, for the heartbeat and the funnel steps. */
let current: { path: string; spaceId: string | null; module: string | null } | null = null
let lastSentPage: string | null = null
let pendingPage: { path: string; spaceId: string | null; module: string | null; contentId: string | null; holdUntil: number } | null = null
let pageTimer: number | null = null

/* The entry's origin, sent once with the first page view of the tab. */
let entry: { referrer?: string; utmSource?: string; utmMedium?: string; utmCampaign?: string } | null = null

const readEntry = () => {
  if (!isBrowser) return
  const result: NonNullable<typeof entry> = {}
  try {
    const ref = document.referrer
    if (ref && new URL(ref).host !== window.location.host) result.referrer = ref.slice(0, 500)
  } catch {
    /* Malformed referrer: none. */
  }
  try {
    const q = new URLSearchParams(window.location.search)
    const utm = (key: string) => q.get(key)?.trim().slice(0, 100) || undefined
    result.utmSource = utm('utm_source')
    result.utmMedium = utm('utm_medium')
    result.utmCampaign = utm('utm_campaign')
  } catch {
    /* No query. */
  }
  entry = result
}

const endpoint = () => `${getApiOrigin()}/api/analytics/events`

const authToken = (): string | null => {
  try {
    return localStorage.getItem('memberfy_auth_token')
  } catch {
    return null
  }
}

/** Sends what is queued. `beacon` when the page is going away (no headers: the community goes in the body). */
const send = (useBeacon = false) => {
  if (flushTimer) {
    window.clearTimeout(flushTimer)
    flushTimer = null
  }
  if (queue.length === 0) return
  const communityId = sdk.getCommunityId()
  /* The community is resolved from the domain a moment after boot: try again later. */
  if (!communityId) {
    if (!useBeacon) scheduleFlush(presenceDelay())
    return
  }
  const sessionToken = readSessionToken()
  /* No token yet and the first batch is still out: wait for it, so the tab keeps one session. */
  if (!sessionToken && awaitingToken && !useBeacon) {
    scheduleFlush(presenceDelay())
    return
  }
  const session = sessionToken ? { sessionId: sessionToken } : {}
  while (queue.length > 0) {
    /* Without a token only one batch goes now; the rest follows with the token it brings back. */
    const events = queue.splice(0, MAX_BATCH)
    try {
      if (useBeacon && typeof navigator.sendBeacon === 'function') {
        const blob = new Blob([JSON.stringify({ communityId, ...session, events })], { type: 'text/plain' })
        if (navigator.sendBeacon(endpoint(), blob)) continue
      }
      const token = authToken()
      const opensSession = !sessionToken
      const sentIn = generation
      if (opensSession) awaitingToken = true
      void fetch(endpoint(), {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          'X-CommunityId': communityId,
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ ...session, events })
      })
        .then((res) => (res.ok ? res.json() : null))
        .then((body: { data?: { sessionToken?: unknown } } | null) => {
          if (sentIn === generation) storeSessionToken(body?.data?.sessionToken)
        })
        .catch(() => undefined)
        .finally(() => {
          if (opensSession && sentIn === generation) awaitingToken = false
        })
      if (opensSession && !useBeacon) {
        if (queue.length > 0) scheduleFlush(presenceDelay())
        return
      }
    } catch {
      /* Analytics never breaks the page. */
    }
  }
}

let flushAt = 0
/* The funnel steps wait for the batch; a queued presence event goes out about a second later. */
const presenceDelay = () => (queue.some((e) => e.type !== 'funnel_step') ? PRESENCE_RETRY_MS : FLUSH_MS)

const scheduleFlush = (delay = FLUSH_MS) => {
  if (!isBrowser) return
  /* A sooner flush replaces a later one; never the other way round. */
  if (flushTimer && flushAt <= Date.now() + delay) return
  if (flushTimer) window.clearTimeout(flushTimer)
  flushAt = Date.now() + delay
  flushTimer = window.setTimeout(() => send(), delay)
}

const enqueue = (event: Omit<AnalyticsEvent, 'occurredAt'>, immediate = false) => {
  if (!isBrowser) return
  start()
  if (queue.length >= MAX_QUEUE) queue.shift()
  queue.push({ ...event, occurredAt: new Date().toISOString() })
  if (immediate || queue.length >= MAX_BATCH) send()
  else scheduleFlush()
}

const heartbeat = () => {
  if (!current || document.visibilityState !== 'visible') return
  enqueue({ type: 'heartbeat', path: current.path, spaceId: current.spaceId, module: current.module }, true)
}

function start() {
  if (started || !isBrowser) return
  started = true
  readEntry()
  window.setInterval(heartbeat, HEARTBEAT_MS)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') send()
    else heartbeat()
  })
  window.addEventListener('pagehide', () => {
    flushPageView(true)
    send(true)
  })
}

/** `/{section}/{space}/{uuid}`: the id of an item page, or null. */
const itemIdOf = (path: string): string | null => {
  const segments = path.split('/').filter(Boolean)
  return segments.length === 3 && UUID.test(segments[2]) ? segments[2] : null
}

/**
 * Sends the settled page view. An item page whose space is still unknown waits a little
 * (unless `force`): its space tells whether it is a post, and the post page's single
 * page view must carry the post (F-179), never a second page view later.
 */
function flushPageView(force = false) {
  if (pageTimer) {
    window.clearTimeout(pageTimer)
    pageTimer = null
  }
  if (!pendingPage) return
  if (!force && !pendingPage.module && itemIdOf(pendingPage.path) && Date.now() < pendingPage.holdUntil) {
    pageTimer = window.setTimeout(() => flushPageView(), POST_HOLD_RETRY_MS)
    return
  }
  const { contentId } = pendingPage
  const page = { path: pendingPage.path, spaceId: pendingPage.spaceId, module: pendingPage.module }
  pendingPage = null
  current = page
  if (page.path === lastSentPage) return
  lastSentPage = page.path
  const origin = entry && Object.values(entry).some(Boolean) ? entry : null
  entry = {}
  /* The first view also counts the visitor as online right away. */
  enqueue({ type: 'page_view', path: page.path, spaceId: page.spaceId, module: page.module, ...(contentId ? { contentId } : {}), ...(origin || {}) }, true)
}

/**
 * A route render, from RouteContext. Like the GA tracker: only a new path is sent, once
 * it has been stable for a moment, with the space it settled on.
 */
export function collectPageView(path: string, space?: { id?: string | null; module?: string | null } | null) {
  if (!isBrowser) return
  start()
  const page = sanitizePath(path)
  const spaceId = space?.id && UUID.test(space.id) ? space.id : null
  if (page === lastSentPage && !pendingPage) {
    /* Same route, its space arrived later: keep the heartbeat accurate. */
    if (current && spaceId && !current.spaceId) current = { ...current, spaceId, module: space?.module || null }
    return
  }
  const module = spaceId ? space?.module || null : null
  const itemId = itemIdOf(page)
  pendingPage = {
    path: page,
    spaceId,
    module,
    contentId: itemId && module && POST_MODULES.has(module) ? itemId : null,
    /* The space arriving later re-reports the same route: it keeps the first deadline. */
    holdUntil: pendingPage?.path === page ? pendingPage.holdUntil : Date.now() + POST_HOLD_MS
  }
  if (pageTimer) window.clearTimeout(pageTimer)
  pageTimer = window.setTimeout(flushPageView, PAGE_VIEW_SETTLE_MS)
}

/** A purchase-funnel step (catalog, product, checkout data, payment). Never with what was typed. */
export function collectFunnelStep(step: FunnelStep, productId?: string | null) {
  if (!isBrowser) return
  if (pendingPage) flushPageView(true)
  const path = current?.path || sanitizePath(window.location.pathname)
  enqueue({
    type: 'funnel_step',
    path,
    spaceId: current?.spaceId || null,
    module: current?.module || null,
    funnelStep: step,
    productId: productId && UUID.test(productId) ? productId : null
  })
}

/**
 * Before the token changes (login, signup): what was collected anonymously goes out
 * as anonymous, so the API can tie this tab's session to the profile on the next batch.
 */
export function flushAnalytics() {
  if (!isBrowser) return
  if (pendingPage) flushPageView(true)
  send()
}

/**
 * Right after sign-in (the new token stored): a heartbeat now, so "Online agora" counts the
 * member at once instead of at the next minute's heartbeat (F-110).
 */
export function collectPresence() {
  if (!isBrowser) return
  start()
  if (pendingPage) flushPageView(true)
  heartbeat()
}

/** Where a post event happened: the settled page, else the address bar. */
const postEventPlace = () => {
  if (current) return { path: current.path, spaceId: current.spaceId, module: current.module }
  return { path: sanitizePath(window.location.pathname), spaceId: null, module: null }
}

/** F-179: a card's "…ver mais" was opened (the card sends it once). */
export function collectPostExpand(contentId: string) {
  if (!isBrowser || !UUID.test(contentId)) return
  enqueue({ type: 'post_expand', ...postEventPlace(), contentId })
}

/**
 * F-179: a link of a post was clicked (its body or a link card). Only the link's origin
 * leaves the browser (never its path or query, which may carry tokens); the API keeps the
 * domain. Sent at once: the click may be leaving the page.
 */
export function collectPostLinkClick(contentId: string, href: string) {
  if (!isBrowser || !UUID.test(contentId)) return
  let url: URL
  try {
    url = new URL(href, window.location.href)
  } catch {
    return
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return
  enqueue({ type: 'post_link_click', ...postEventPlace(), contentId, linkHost: url.origin }, true)
}

/** Logout: what is queued goes as the person who did it, then the tab starts a new session. */
export function resetAnalyticsSession() {
  if (!isBrowser) return
  flushAnalytics()
  generation++
  awaitingToken = false
  clearSessionToken()
  /* The next page view of the new session is sent even on the same route. */
  lastSentPage = null
}
